import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiMail, FiPhone, FiClock, FiSend, FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';

// Firebase Analytics
import { analytics, logEvent } from '../firebase';
import { shouldShowAnalytics } from '../utils/cookieConsent';

// EmailJS Configuratie
import { EMAILJS_CONFIG } from '../config/emailjs';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Form state interface
interface FormState {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const Contactformnieuwlid: React.FC = () => {
  // Google Analytics - pagina view
  useEffect(() => {
    if (analytics && shouldShowAnalytics()) {
      logEvent(analytics, 'page_view', {
        page_title: 'Contactformulier nieuwe leden',
        page_location: window.location.href,
        page_path: '/contactform-nieuwlid'
      });
    }
  }, []);

  const [formState, setFormState] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Telefoonnummer validatie functie
  const validatePhoneNumber = (phone: string): boolean => {
    // Verwijder alle niet-numerieke karakters
    const cleanPhone = phone.replace(/\D/g, '');
    // Controleer of het exact 10 cijfers heeft
    return cleanPhone.length === 10;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    // Telefoonnummer validatie
    if (!validatePhoneNumber(formState.phone)) {
      setIsSubmitting(false);
      setSubmitError('Voer een geldig Nederlands telefoonnummer in (10 cijfers, bijvoorbeeld: 0612345678)');
      return;
    }

    // Google Analytics - form submission
    if (analytics && shouldShowAnalytics()) {
      logEvent(analytics, 'form_submit', {
        form_name: 'nieuwe_leden_aanmelding',
        form_id: 'contactform-nieuwlid',
        subject: formState.subject
      });
    }

    // EmailJS configuratie
    const templateParams = {
      from_name: formState.name,
      from_email: formState.email,
      from_phone: formState.phone,
      subject: formState.subject,
      message: formState.message,
      to_email: EMAILJS_CONFIG.TO_EMAIL
    };

    // EmailJS verzenden
    emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      templateParams,
      EMAILJS_CONFIG.PUBLIC_KEY
    )
      .then((response) => {
        console.log('Email succesvol verzonden:', response);
        setIsSubmitting(false);
        setSubmitSuccess(true);
        setFormState({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        // Reset success message na 5 seconden
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      })
      .catch((error) => {
        console.error('Email verzending mislukt:', error);
        setIsSubmitting(false);
        setSubmitError('Er is een fout opgetreden bij het verzenden van het formulier. Probeer het later opnieuw.');
      });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-800 to-blue-900">
      {/* Terug knop */}
      <div className="absolute top-6 left-6 z-20">
        <Link 
          to="/" 
          className="inline-flex items-center text-white hover:text-teal-400 transition-colors duration-300"
        >
          <FiArrowLeft className="mr-2" />
          Terug naar hoofdpagina
        </Link>
      </div>

      {/* Hoofdinhoud */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-20">
        <div className="w-full max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Hoofdtitel */}
            <motion.div 
              variants={fadeIn}
              className="text-center mb-12"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Meer weten over <span className="text-teal-400">Lionsclub Hoorn de Doelen</span>?
              </h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                Vul het onderstaande formulier in om meer informatie te ontvangen of om een vrijblijvende kennismaking aan te vragen.
              </p>
            </motion.div>

            {/* Contact Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Informatie */}
              <motion.div
                variants={fadeIn}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20"
              >
                <h2 className="text-3xl font-bold mb-8 text-white">
                  Contactgegevens
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-teal-500 rounded-full p-3 text-white">
                      <FiMapPin size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">Adres</h3>
                      <p className="text-gray-200">
                        Restaurant De Nadorst<br />
                        Westerblokker 171<br />
                        1695AE Blokker
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-teal-500 rounded-full p-3 text-white">
                      <FiMail size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">E-mail</h3>
                      <p className="text-gray-200">
                        <a href="mailto:secretaris@mail.lionshoorn.nl" className="hover:text-teal-400 transition-colors duration-300">
                          secretaris@mail.lionshoorn.nl
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-teal-500 rounded-full p-3 text-white">
                      <FiPhone size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">Telefoon</h3>
                      <p className="text-gray-200">
                        <a href="tel:+31614267886" className="hover:text-teal-400 transition-colors duration-300">
                          06-14267886
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-teal-500 rounded-full p-3 text-white">
                      <FiClock size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">Bijeenkomsten</h3>
                      <p className="text-gray-200">
                        2e maandag van de maand: 18:00 uur<br />
                        4e maandag van de maand: 18:00 uur
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4 text-white">Locatie</h3>
                  <div className="rounded-lg overflow-hidden shadow-lg h-64">
                    <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2421.0982618030934!2d5.1022!3d52.6567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c8a8e8e8e8e8e8%3A0x8e8e8e8e8e8e8e8e!2sRestaurant%20De%20Nadorst!5e0!3m2!1snl!2snl!4v1625000000000!5m2!1snl!2snl" 
                      width="100%" 
                      height="100%" 
                      style={{ border: 0 }} 
                      allowFullScreen 
                      loading="lazy"
                      title="Locatie Lions Club Hoorn De Doelen"
                    ></iframe>
                  </div>
                </div>
              </motion.div>

              {/* Contact Formulier */}
              <motion.div
                variants={fadeIn}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20"
              >
                <h2 className="text-3xl font-bold mb-8 text-white">
                  Aanmeld / vragen formulier
                </h2>
                <form 
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div>
                    <label htmlFor="name" className="block text-white mb-2">Naam *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="Uw volledige naam"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-white mb-2">E-mail *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="uw.email@voorbeeld.nl"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-white mb-2">Telefoonnummer *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formState.phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9\-\s\(\)\+]{10,}"
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="06-12345678"
                      title="Voer een geldig Nederlands telefoonnummer in (10 cijfers)"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-white mb-2">Onderwerp *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formState.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                    >
                      <option value="">Selecteer een onderwerp</option>
                      <option value="Lid worden">Lid worden</option>
                      <option value="Kennismaking">Kennismaking</option>
                      <option value="Meer informatie">Meer informatie</option>
                      <option value="Vragen over lidmaatschap">Vragen over lidmaatschap</option>
                      <option value="Anders">Anders</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-white mb-2">Bericht *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                      placeholder="Vertel ons waarom u interesse heeft in lidmaatschap..."
                    ></textarea>
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <span>Verzenden...</span>
                      ) : (
                        <>
                          <FiSend className="mr-2" />Verstuur
                        </>
                      )}
                    </button>
                  </div>
                  {submitSuccess && (
                    <div className="bg-green-500/20 border border-green-400 text-green-200 px-4 py-3 rounded">
                      <p>Bedankt voor uw vraag! We nemen binnen 2 werkdagen contact met u op.</p>
                    </div>
                  )}
                  {submitError && (
                    <div className="bg-red-500/20 border border-red-400 text-red-200 px-4 py-3 rounded">
                      <p>{submitError}</p>
                    </div>
                  )}
                </form>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contactformnieuwlid; 